import type { ReactNode } from 'react';
import { Card, Switch } from '@heroui/react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/**
 * Сгруппированный список в духе iOS Settings — общий у «Настроек» и «Профиля».
 * Заголовок капсом + одна скруглённая карточка, строки внутри разделены
 * волосяной линией. Без заголовка — просто отдельная карточка.
 */
export function SettingsGroup({ header, children }: { header?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      {header && (
        <h2 className="px-1 text-[11px] font-semibold tracking-wide text-ink-subtle uppercase">
          {header}
        </h2>
      )}
      <Card className="divide-y divide-hairline gap-0 overflow-hidden p-0">{children}</Card>
    </section>
  );
}

interface SettingsRowProps {
  icon: LucideIcon;
  /** Фон цветного квадрата с иконкой — фиксированный цвет, как в iOS Settings, не завязан на тему. */
  iconClassName: string;
  label: string;
  /** Строка-навигация: показывает текущее значение (если есть) и шеврон, тап вызывает `onClick`. */
  value?: string;
  onClick?: () => void;
  /** Строка видна, но приглушена и не нажимается — и строка-навигация, и свитчер. */
  disabled?: boolean;
  /** Строка-переключатель: вместо значения и шеврона — свитчер. */
  switchValue?: boolean;
  onSwitchChange?: (value: boolean) => void;
}

export function SettingsRow({
  icon: Icon,
  iconClassName,
  label,
  value,
  onClick,
  disabled,
  switchValue,
  onSwitchChange,
}: SettingsRowProps) {
  const content = (
    <>
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-[10px] text-white',
          iconClassName,
        )}
      >
        <Icon className="size-[18px]" aria-hidden="true" />
      </span>
      <span className="flex-1 text-left text-[15px] font-medium text-ink">{label}</span>
      {onSwitchChange !== undefined ? (
        <Switch
          className="settings-switch"
          aria-label={label}
          isSelected={switchValue}
          isDisabled={disabled}
          onChange={onSwitchChange}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      ) : (
        <>
          {value && <span className="text-[15px] text-ink-muted">{value}</span>}
          <ChevronRight className="size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
        </>
      )}
    </>
  );

  if (onSwitchChange !== undefined) {
    return (
      <div className={cn('flex items-center gap-3 px-4 py-3', disabled && 'opacity-45')}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 px-4 py-3 active:bg-glass-2 disabled:opacity-45 disabled:active:bg-transparent"
    >
      {content}
    </button>
  );
}
